import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PageLayout from '../pagelayout';
import Calendar from '../components/calendar';
import * as css from './calendarpsa.module.scss';
import { ActivityStatusOptions } from './calendarModels';
import {
  Outlet,
  useNavigate,
  useParams,
  Routes,
  Route,
} from 'react-router-dom';
import classNames from 'classnames';
import { DropdownButton } from '../components/button/dropdownButton';
import useCalendarNavigation, {
  CalendarNavigationContext,
  ZoomFactor,
} from '../components/calendar/useCalendarNavigation';
import Activity from '../practiceactivity/activity.psa';
import PageNotFoundPage from '../pages/pagenotfound';
import { getAndSetCurrentYearFromSessionStorage } from '../components/calendar/utils';
import CalendarDownload, {
  CalendarDownloadApiType,
} from '../components/calendar/calendardownload';
import Search from '../components/calendar/search';

const CalendarAndActivityPage = () => (
  <Routes>
    <Route path="/" element={<Outlet />}>
      <Route path=":startYear?" element={<CalendarPage />} />
      <Route path="activity/:activityId/*" element={<Activity />} />
      <Route path="*" element={<PageNotFoundPage />} />
    </Route>
  </Routes>
);

const CalendarPage = () => {
  const { formatMessage } = useIntl();
  const { startYear } = useParams<{ startYear: string }>();
  const currentYear = getAndSetCurrentYearFromSessionStorage(startYear);
  const [searchPhrase, setSearchPhrase] = React.useState(
    sessionStorage.getItem('PSACalendarSearchPhrase') || '',
  );
  const [searchCssOutlined, setSearchCssOutlined] = React.useState(false);
  const [searchClearButton, setSearchClearButton] = React.useState(
    searchPhrase.length > 0,
  );
  const [activityStatusFilter, setActivityStatusFilter] = React.useState(
    (sessionStorage.getItem(
      'PSAActivityStatusFilter',
    ) as ActivityStatusOptions) || ActivityStatusOptions.UNFILTERED,
  );

  const navigate = useNavigate();
  const [calendarNavigation, calendarNavigationDispatch] =
    useCalendarNavigation();

  const linkYear = formatMessage({
    id: 'calendarpsa.linkYear',
    defaultMessage: 'Year',
  });

  const pageTitle1 = formatMessage({
    id: 'calendaruha.titlePart1',
    defaultMessage: '[!] Practice Calendar',
  });
  const pageTitle2 = formatMessage(
    { id: 'calendaruha.titlePart2' },
    { year: `${currentYear}/${currentYear + 1}` },
  );

  const pageSubtitle = formatMessage({
    id: 'calendarpsa.subtitle',
    defaultMessage:
      '[!] Besvar kapasitet for alle praksisaktivitetene i kalenderen under',
  });

  const emptyStateTitle = formatMessage({
    id: 'calendarpsa.emptyStateTitle',
  });
  const emptyStateText = formatMessage({
    id: 'calendarpsa.emptyStateText',
  });

  const handleMenuClick = (year: number) => {
    navigate(`/calendar/${year}`);
  };

  const titleDropdownElements = [
    {
      text: `${linkYear} ${currentYear - 1}/${currentYear}`,
      onChange: () => handleMenuClick(currentYear - 1),
    },
    {
      text: `${linkYear} ${currentYear}/${currentYear + 1}`,
      onChange: () => {},
      disabled: true,
    },
    {
      text: `${linkYear} ${currentYear + 1}/${currentYear + 2}`,
      onChange: () => handleMenuClick(currentYear + 1),
    },
  ];

  const titleAction = (
    <DropdownButton
      id={'calendartitle'}
      btnText={pageTitle2}
      options={titleDropdownElements}
    />
  );

  useEffect(
    () =>
      sessionStorage.setItem('PSAActivityStatusFilter', activityStatusFilter),
    [activityStatusFilter],
  );

  useEffect(
    () => sessionStorage.setItem('PSACalendarSearchPhrase', searchPhrase),
    [searchPhrase],
  );

  const actionItems = useMemo(
    () => (
      <CalendarDownload
        startYear={currentYear}
        api={CalendarDownloadApiType.PraksisstedApi}
      />
    ),
    [currentYear],
  );

  return (
    <PageLayout
      documentTitle={`${pageTitle1} ${pageTitle2}`}
      isIndented={false}
      title={pageTitle1}
      titleActions={titleAction}
      actionItems={actionItems}
      subtitle={pageSubtitle}
      isFullScreen
    >
      <div
        className={classNames(
          css.calendarContentsWrapper,
          calendarNavigation.zoomFactor === ZoomFactor.WEEKLY
            ? 'weekly'
            : 'daily',
        )}
      >
        <CalendarNavigationContext.Provider value={calendarNavigationDispatch}>
          <Search
            onSearchPhraseChanged={(val: string) => setSearchPhrase(val)}
            searchPhrase={searchPhrase}
            navigation={calendarNavigation}
            searchCssOutlined={searchCssOutlined}
            changeSearchCssOutlined={setSearchCssOutlined}
            showClearButton={searchClearButton}
            toggleClearButton={setSearchClearButton}
            onActivityStatusFilterChanged={setActivityStatusFilter}
            activityStatusFilter={activityStatusFilter}
            isStudiested={false}
          />
          <Calendar
            emptyStateTitle={emptyStateTitle}
            emptyStateText={emptyStateText}
            startYear={currentYear}
            navigation={calendarNavigation}
            searchPhrase={searchPhrase}
            activityStatusFilter={activityStatusFilter}
            onActivityStatusFilterChanged={setActivityStatusFilter}
          />
        </CalendarNavigationContext.Provider>
      </div>
    </PageLayout>
  );
};

export default CalendarAndActivityPage;
